import React, { useEffect, useState } from "react";
import { handleOutsideClick } from "../../../Helpers/HandleOutsideClick";
import { Info, X } from "lucide-react";

const SubmitCandidateModal = ({
  show,
  onClose,
  onSubmit,
  sendFormData,
  handleSendChange,
  errors,
  gender,
  joblocations,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(show);
  const [isLocationOpen, setIsLocationOpen] = useState(false);

  const numberToIndianWords = (num) => {
    if (!num) return "";

    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const getWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + " " + ones[n % 10];
      if (n < 1000)
        return ones[Math.floor(n / 100)] + " Hundred " + getWords(n % 100);
      return "";
    };

    const lakh = Math.floor(num / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const remainder = num % 1000;

    let result = "";

    if (lakh) result += getWords(lakh) + " Lakh ";
    if (thousand) result += getWords(thousand) + " Thousand ";
    if (remainder) result += getWords(remainder);

    return result.trim();
  };

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      setTimeout(() => setShouldRender(false), 400);
    }
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".location-dropdown")) {
        setIsLocationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-[1050] flex items-end lg:items-stretch justify-center lg:justify-end transition-opacity duration-300 ${
        isVisible ? "bg-black/40 opacity-100" : "bg-black/0 opacity-0"
      }`}
      onClick={(e) => handleOutsideClick(e, onClose)}
    >
      {/* Drawer */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white h-full w-full sm:w-[90%] lg:w-[65%] rounded-t-2xl lg:rounded-none shadow-2xl flex flex-col transform transition-transform duration-300 ${
          isVisible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b">
          <h2 className="text-lg font-semibold text-[#0467c5]">
            Candidate Details
          </h2>

          <button onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <form
            onSubmit={onSubmit}
            noValidate
            className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6"
          >
            {/* Current CTC */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Current CTC <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="CurrentCTC"
                placeholder="Current CTC"
                min={0}
                value={sendFormData.CurrentCTC}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleSendChange(e);
                  }
                }}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-300"
              />

              {sendFormData.CurrentCTC && (
                <p className="text-xs text-green-600 mt-1 italic">
                  {numberToIndianWords(Number(sendFormData.CurrentCTC))}
                </p>
              )}

              {errors.CurrentCTC && (
                <p className="text-red-500 text-xs mt-1">{errors.CurrentCTC}</p>
              )}
            </div>

            {/* Expected CTC */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Expected CTC <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="ExpectedCTC"
                placeholder="Expected CTC"
                min={0}
                value={sendFormData.ExpectedCTC}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleSendChange(e);
                  }
                }}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {sendFormData.ExpectedCTC && (
                <p className="text-xs text-green-600 mt-1 italic">
                  {numberToIndianWords(Number(sendFormData.ExpectedCTC))}
                </p>
              )}

              {errors.ExpectedCTC && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.ExpectedCTC}
                </p>
              )}
            </div>

            {/* Total Experience */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Total Experience (Years) <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="TotalExperience"
                placeholder="Total Experience"
                min={0}
                value={sendFormData.TotalExperience}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    handleSendChange(e);
                  }
                }}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {errors.TotalExperience && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.TotalExperience}
                </p>
              )}
            </div>

            {/* Domain Experience */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Domain Experience (Years){" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="DomainExperience"
                placeholder="Domain Experience"
                min={0}
                value={sendFormData.DomainExperience}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    handleSendChange(e);
                  }
                }}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {errors.DomainExperience && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.DomainExperience}
                </p>
              )}
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Gender <span className="text-red-500">*</span>
              </label>

              <select
                name="Gender"
                value={sendFormData.Gender || ""}
                onChange={handleSendChange}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select</option>

                {(Array.isArray(gender) ? gender : []).map((g) => (
                  <option key={g.id} value={g.genderType}>
                    {g.genderType}
                  </option>
                ))}
              </select>

              {errors.Gender && (
                <p className="text-red-500 text-sm mt-1">{errors.Gender}</p>
              )}
            </div>

            {/* Current Location */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Current Location <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="CurrentLocation"
                placeholder="Current Location"
                value={sendFormData.CurrentLocation}
                onChange={handleSendChange}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:ring-2 focus:ring-blue-300"
              />
              {errors.CurrentLocation && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.CurrentLocation}
                </p>
              )}
            </div>

            {/* Preferred Location */}
            <div className="relative location-dropdown">
              <label className="block text-sm font-medium mb-1">
                Preferred Location <span className="text-red-500">*</span>
              </label>

              {/* Dropdown Box */}
              <div
                onClick={() => setIsLocationOpen(!isLocationOpen)}
                className="w-full px-4 py-2 border rounded-md cursor-pointer bg-white"
              >
                {sendFormData.PreferredLocation?.length > 0
                  ? sendFormData.PreferredLocation.join(", ")
                  : "Select Locations"}
              </div>

              {/* Dropdown List */}
              {isLocationOpen && (
                <div
                  className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto border rounded-md bg-white shadow"
                  onClick={(e) => e.stopPropagation()}
                >
                  {(Array.isArray(joblocations) ? joblocations : []).map(
                    (loc) => (
                      <label
                        key={loc.id}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={sendFormData.PreferredLocation?.includes(
                            loc.jobLocation,
                          )}
                          onChange={(e) => {
                            let updatedLocations = [
                              ...(sendFormData.PreferredLocation || []),
                            ];

                            if (e.target.checked) {
                              updatedLocations.push(loc.jobLocation);
                            } else {
                              updatedLocations = updatedLocations.filter(
                                (l) => l !== loc.jobLocation,
                              );
                            }

                            handleSendChange({
                              target: {
                                name: "PreferredLocation",
                                value: updatedLocations,
                              },
                            });
                          }}
                        />
                        <span>{loc.jobLocation}</span>
                      </label>
                    ),
                  )}
                </div>
              )}

              {errors.PreferredLocation && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.PreferredLocation}
                </p>
              )}
            </div>

            {/* Current Company */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-1">
                Current Company <span className="text-red-500">*</span>
                {/* Info Icon ONLY hover trigger */}
                <div className="relative inline-block group cursor-pointer">
                  <Info className="w-4 h-4 text-blue-500" />

                  {/* Tooltip */}
                  <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-80 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 pointer-events-none group-hover:opacity-100 transition duration-200 z-50 shadow-lg">
                    Enter your current employer name. If you have no prior work
                    experience, you may enter{" "}
                    <span className="font-semibold">"Fresher"</span>.
                    {/* Arrow */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                </div>
              </label>

              <input
                type="text"
                name="CurrentCompany"
                placeholder="Current Company"
                value={sendFormData.CurrentCompany}
                onChange={handleSendChange}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />

              {errors.CurrentCompany && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.CurrentCompany}
                </p>
              )}
            </div>

            {/* Notice Period */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Notice Period (Days) <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="NoticePeriod"
                placeholder="Notice Period"
                min={0}
                value={sendFormData.NoticePeriod}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleSendChange(e);
                  }
                }}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {errors.NoticePeriod && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.NoticePeriod}
                </p>
              )}
            </div>

            {/* Qualification */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Qualification <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="Qualification"
                placeholder="Qualification"
                value={sendFormData.Qualification}
                onChange={handleSendChange}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {errors.Qualification && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.Qualification}
                </p>
              )}
            </div>

            {/* Ready to Relocate */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Ready to Relocate <span className="text-red-500">*</span>
              </label>

              <select
                name="ReadytoRelocate"
                value={sendFormData.ReadytoRelocate}
                onChange={handleSendChange}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Applicable">Not Applicable</option>
              </select>
              {errors.ReadytoRelocate && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.ReadytoRelocate}
                </p>
              )}
            </div>

            {/* Reason of Leaving */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Reason of Leaving <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="ReasonofLeaving"
                placeholder="Reason of Leaving"
                value={sendFormData.ReasonofLeaving}
                onChange={handleSendChange}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {errors.ReasonofLeaving && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.ReasonofLeaving}
                </p>
              )}
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Remarks <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="Remarks"
                placeholder="Remarks"
                value={sendFormData.Remarks}
                onChange={handleSendChange}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {errors.Remarks && (
                <p className="text-red-500 text-xs mt-1">{errors.Remarks}</p>
              )}
            </div>

            {/* Resume */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Resume <span className="text-red-500">*</span>
              </label>

              <input
                type="file"
                name="ResumeFile"
                accept=".pdf,application/pdf"
                onChange={handleSendChange}
                className="w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {errors.ResumeFile && (
                <p className="text-red-500 text-sm mt-1">{errors.ResumeFile}</p>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 px-4 sm:px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 border rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={onSubmit}
            className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Send Candidate
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmitCandidateModal;
